import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Phone, Menu, RotateCcw, ArrowRight, MessageSquare } from "lucide-react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logoImage from "@/assets/lifetime-movers-logo-transparent.png";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleGetQuote = () => {
    navigate('/quote');
  };

  return (
    <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-secondary focus:text-secondary-foreground focus:rounded-md focus:font-semibold focus:shadow-lg"
    >
      Skip to main content
    </a>
    <header className="fixed top-0 left-0 right-0 z-50 bg-muted/95 border-b-2 border-primary/30 shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <div className="flex items-center mr-8">
            <a href="/" className="flex items-center">
              <img 
                src={logoImage} 
                alt="LifeTime Movers Logo" 
                width={160}
                height={96}
                className="h-24 w-auto hover:opacity-90 transition-opacity"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-5 ml-auto mr-8">
            <a href="/" className="text-foreground hover:text-secondary transition-colors font-medium">
              Home
            </a>
            <a href="/moving-services" className="text-foreground hover:text-secondary transition-colors font-medium">
              Moving Services
            </a>
            <a href="/service-areas" className="text-foreground hover:text-secondary transition-colors font-medium">
              Service Areas
            </a>
            <a href="/about-us" className="text-foreground hover:text-secondary transition-colors font-medium">
              About
            </a>
            <a href="/faq" className="text-foreground hover:text-secondary transition-colors font-medium">
              FAQ
            </a>
            <a href="/lifetime-movers-reviews" className="text-foreground hover:text-secondary transition-colors font-medium">
              Reviews
            </a>
            <a href="/blog" className="text-foreground hover:text-secondary transition-colors font-medium">
              Blog
            </a>
          </nav>

          {/* Contact & CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <div className="flex flex-col items-center">
              <a href="tel:+16306869987" className="flex items-center gap-2 text-foreground hover:text-secondary transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-semibold">(630) 686-9987</span>
              </a>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Call or Text for Assistance
              </span>
            </div>
            <Button 
              size="default" 
              onClick={handleGetQuote}
              className="bg-secondary hover:bg-secondary/90 text-white font-semibold transition-colors px-4"
            >
              Get Instant Estimate
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="default" 
              onClick={() => navigate('/resume-booking')}
              className="px-4"
            >
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Resume Booking
            </Button>
          </div>

          {/* Mobile Phone CTA & Menu */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Click-to-Call Button */}
            <a
              href="tel:+16306869987"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-secondary text-white hover:bg-secondary/90 transition-colors"
              aria-label="Call or Text (630) 686-9987"
            >
              <div className="flex items-center gap-0.5">
                <Phone className="h-4 w-4" />
                <MessageSquare className="h-4 w-4" />
              </div>
              <span className="text-sm font-semibold">(630) 686-9987</span>
            </a>
            
            {/* Hamburger Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  className="p-2"
                  aria-label="Toggle menu"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-[300px] sm:w-[400px]"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 80px,
                  hsl(var(--primary) / 0.08) 80px,
                  hsl(var(--primary) / 0.08) 160px
                )`
              }}
            >
              <nav aria-label="Mobile navigation" className="flex flex-col gap-4 mt-8">
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/');
                  }}
                >
                  Home
                </button>
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/moving-services');
                  }}
                >
                  Moving Services
                </button>
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/service-areas');
                  }}
                >
                  Service Areas
                </button>
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/about-us');
                  }}
                >
                  About
                </button>
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/faq');
                  }}
                >
                  FAQ
                </button>
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/lifetime-movers-reviews');
                  }}
                >
                  Reviews
                </button>
                <button
                  className="text-left text-foreground hover:text-secondary transition-colors font-medium py-2"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/blog');
                  }}
                >
                  Blog
                </button>
                <a href="tel:+16306869987" className="flex items-center gap-2 text-foreground hover:text-secondary transition-colors py-2">
                  <Phone className="h-4 w-4" />
                  <span className="font-semibold">(630) 686-9987</span>
                </a>
                <Button 
                  size="lg" 
                  className="w-full mt-4 bg-secondary hover:bg-secondary/90 text-white font-semibold transition-colors"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleGetQuote();
                  }}
                >
                  Get Instant Estimate
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full"
                  onClick={() => {
                    navigate('/resume-booking');
                    setMobileMenuOpen(false);
                  }}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Resume Booking
                </Button>
              </nav>
            </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
    </>
  );
};
